import React, { useEffect, useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';
import { billingService } from '../../api/services/billing.service';
import {
  ServiceRateDTO,
  CreateServiceRateRequest,
  ServiceType,
  serviceTypeLabels,
  BillingBasis,
  billingBasisLabels,
  BILLING_CURRENCIES,
  formatMoney,
} from '../../types/billing';
import { istNowLocal, istToServerUtc, serverUtcToIstLocal } from './closeBooksRules';
import { SkeletonTable } from '../../components/Common/Skeleton';

/** Voice rates bill by the minute and can carry a pulse, a basis and a short-call discount. */
const isVoice = (t: ServiceType) => t === ServiceType.VOICE_INBOUND || t === ServiceType.VOICE_OUTBOUND;

const ServiceRates: React.FC = () => {
  const [rates, setRates] = useState<ServiceRateDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingRate, setEditingRate] = useState<ServiceRateDTO | null>(null);

  const [formData, setFormData] = useState<CreateServiceRateRequest>({
    serviceType: ServiceType.VOICE_OUTBOUND,
    ratePerUnit: 0,
    effectiveFrom: istNowLocal(),
    unitDescription: 'per minute',
    currency: 'INR',
    billingIncrement: 60,
  });

  useEffect(() => {
    loadRates();
  }, [page]);

  const loadRates = async () => {
    setLoading(true);
    const response = await billingService.getServiceRates({ page, size: 20 });
    if (response.success && response.data) {
      setRates(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let response;
    // The form shows IST; the server stores effective_from/effective_to as UTC LocalDateTimes.
    const payload = {
      ...formData,
      effectiveFrom: istToServerUtc(formData.effectiveFrom) || formData.effectiveFrom,
      effectiveTo: formData.effectiveTo ? istToServerUtc(formData.effectiveTo) || formData.effectiveTo : undefined,
    };
    if (editingRate) {
      response = await billingService.updateServiceRate(editingRate.id, payload);
    } else {
      response = await billingService.createServiceRate(payload);
    }

    if (response.success) {
      setShowModal(false);
      setEditingRate(null);
      resetForm();
      loadRates();
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this rate?')) {
      const response = await billingService.deleteServiceRate(id);
      if (response.success) {
        loadRates();
      }
    }
  };

  const resetForm = () => {
    setFormData({
      serviceType: ServiceType.VOICE_OUTBOUND,
      ratePerUnit: 0,
      effectiveFrom: istNowLocal(),
      unitDescription: 'per minute',
      currency: 'INR',
      billingIncrement: 60,
    });
  };

  const openEditModal = (rate: ServiceRateDTO) => {
    setEditingRate(rate);
    setFormData({
      organizationId: rate.organizationId,
      serviceType: rate.serviceType,
      ratePerUnit: rate.ratePerUnit,
      unitDescription: rate.unitDescription,
      minimumCharge: rate.minimumCharge,
      billingIncrement: rate.billingIncrement,
      shortCallSeconds: rate.shortCallSeconds,
      shortCallDiscountPercent: rate.shortCallDiscountPercent,
      billingBasis: rate.billingBasis,
      currency: rate.currency,
      effectiveFrom: serverUtcToIstLocal(rate.effectiveFrom),
      effectiveTo: rate.effectiveTo ? serverUtcToIstLocal(rate.effectiveTo) : undefined,
      description: rate.description,
    });
    setShowModal(true);
  };

  /** "60s pulse · duration · <3s 100% off": a voice rate's terms in one line. */
  const voiceTerms = (rate: ServiceRateDTO) => {
    if (!isVoice(rate.serviceType)) return '-';
    const parts = [`${rate.billingIncrement || 60}s pulse`,
      rate.billingBasis === BillingBasis.TALK_TIME ? 'talk time' : 'duration'];
    if (rate.shortCallSeconds && rate.shortCallDiscountPercent) {
      parts.push(`<${rate.shortCallSeconds}s ${rate.shortCallDiscountPercent}% off`);
    }
    return parts.join(' · ');
  };

  if (loading && rates.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Service Rates</h1>
        <SkeletonTable rows={10} columns={6} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Service Rates</h1>
        <button
          onClick={() => {
            resetForm();
            setEditingRate(null);
            setShowModal(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Rate
        </button>
      </div>

      {/* Rates Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Service Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Voice terms
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {rates.map((rate) => (
                <tr key={rate.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {serviceTypeLabels[rate.serviceType] || rate.serviceType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {rate.organizationName || 'Default'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatMoney(rate.ratePerUnit, rate.currency, 2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {rate.unitDescription || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{voiceTerms(rate)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        rate.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {rate.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEditModal(rate)}
                        className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rate.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing {rates.length} of {totalElements} rates
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">
              {editingRate ? 'Edit Service Rate' : 'Create Service Rate'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Service Type *
                </label>
                <select
                  value={formData.serviceType}
                  onChange={(e) =>
                    setFormData({ ...formData, serviceType: e.target.value as ServiceType })
                  }
                  disabled={!!editingRate}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 dark:disabled:bg-gray-600"
                >
                  {Object.entries(serviceTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization ID (leave empty for default rate)
                </label>
                <input
                  type="text"
                  value={formData.organizationId || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, organizationId: e.target.value || undefined })
                  }
                  placeholder="Leave empty for default/global rate"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rate Per Unit *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  required
                  min="0"
                  value={formData.ratePerUnit}
                  onChange={(e) =>
                    setFormData({ ...formData, ratePerUnit: parseFloat(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Currency
                </label>
                <select
                  value={formData.currency || 'INR'}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {BILLING_CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unit Description
                </label>
                <input
                  type="text"
                  value={formData.unitDescription || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, unitDescription: e.target.value })
                  }
                  placeholder="e.g., per minute, per message"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Charge
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.minimumCharge || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, minimumCharge: parseFloat(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Increment (seconds)
                </label>
                <input
                  type="number"
                  value={formData.billingIncrement || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, billingIncrement: parseInt(e.target.value) })
                  }
                  placeholder="60 = per minute (61s bills as 2 min), 30 = 30-second pulse"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {isVoice(formData.serviceType) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing basis
                    </label>
                    <select
                      value={formData.billingBasis || BillingBasis.DURATION}
                      onChange={(e) =>
                        setFormData({ ...formData, billingBasis: e.target.value as BillingBasis })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {Object.entries(billingBasisLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Short call under (seconds)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.shortCallSeconds ?? ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            shortCallSeconds: e.target.value === '' ? undefined : parseInt(e.target.value),
                          })
                        }
                        placeholder="e.g., 3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.shortCallDiscountPercent ?? ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            shortCallDiscountPercent: e.target.value === '' ? undefined : parseFloat(e.target.value),
                          })
                        }
                        placeholder="100 = free"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    A business's own short-call discount also applies on its provider's bill for its calls.
                    To remove a discount, set it to 0%.
                  </p>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective From (IST) *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.effectiveFrom?.slice(0, 16) || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, effectiveFrom: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective To (IST)
                </label>
                <input
                  type="datetime-local"
                  value={formData.effectiveTo?.slice(0, 16) || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, effectiveTo: e.target.value || undefined })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={formData.description || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingRate(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tl-btn tl-btn-primary"
                >
                  {editingRate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRates;
